<script setup lang="ts">
import { ref } from "vue";
import { Training } from "@/types/training/Training";
import AppButton from "@/components/atoms/button/AppButton.vue";
import { computed } from "vue";
import FormCheckboxGroup from "@/components/molecules/form/FormCheckboxGroup.vue";
import FormRadioGroup from "@/components/molecules/form/FormRadioGroup.vue";
import NextModal from "./NextModal.vue";

const props = defineProps({
  training: {
    type: Object as () => Training,
    required: true
  },
  transitionDisabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits({
  finish: () => true
});

const currentPage = ref(0);
const nextModal = ref<InstanceType<typeof NextModal> | null>(null);
const selectedMAChoiceIds = ref<string[]>([]);
const selectedSAChoiceId = ref("");
const isChoiceSelected = computed(() => {
  if (props.training.questions[currentPage.value].type === "SA") {
    return selectedSAChoiceId.value !== "";
  } else {
    return selectedMAChoiceIds.value.length > 0;
  }
});

const currentQuestion = computed(() => {
  return props.training.questions[currentPage.value];
});
const choiceOptions = computed(() => {
  return currentQuestion.value?.choices.map(c => ({
    value: c.id.toString(),
    text: c.text
  }));
});

function isAnswerCorrect(): boolean {
  const question = currentQuestion.value;
  if (!question) return false;

  if (question.type === "SA") {
    return (
      question.choices.find(c => c.isCorrect)?.id.toString() ===
      selectedSAChoiceId.value
    );
  } else {
    const correctChoices = question.choices
      .filter(c => c.isCorrect)
      .map(c => c.id.toString());
    return (
      selectedMAChoiceIds.value.length === correctChoices.length &&
      selectedMAChoiceIds.value.every(v => correctChoices.includes(v))
    );
  }
}

async function onClickNext() {
  if (!isAnswerCorrect()) {
    await nextModal.value?.show(false);
    return;
  }

  await nextModal.value?.show(true);

  // プレビューの場合は遷移を無効にする
  if (props.transitionDisabled) return;

  if (currentPage.value === props.training.questions.length - 1) {
    emit("finish");
    return;
  }

  currentPage.value++;
  selectedMAChoiceIds.value = [];
  selectedSAChoiceId.value = "";
}
</script>

<template>
  <div v-if="training && currentQuestion" class="training">
    <div class="header">
      <h3>{{ training.title }}</h3>
      <div class="question-count">
        {{ currentPage + 1 }}/{{ training.questions.length }}問
      </div>
    </div>
    <div class="content">
      <div class="question">
        {{ currentQuestion.text }}
      </div>
      <div
        class="choices"
        :class="{
          sa: currentQuestion.type === 'SA',
          ma: currentQuestion.type === 'MA'
        }"
      >
        <template v-if="currentQuestion.type === 'MA'">
          <FormCheckboxGroup
            :options="choiceOptions"
            v-model="selectedMAChoiceIds"
            variant="stacked"
          />
        </template>
        <template v-else>
          <FormRadioGroup
            :name="currentQuestion.id.toString()"
            v-model="selectedSAChoiceId"
            :options="choiceOptions"
            flex-direction="column"
            variant="stacked"
          />
        </template>
      </div>
    </div>
    <div class="bottom">
      <AppButton
        @click="onClickNext"
        class="next-button"
        :disabled="!isChoiceSelected"
        >次へ</AppButton
      >
      <NextModal ref="nextModal" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.app-radio__label) {
  font-size: 14px !important;
  background-color: transparent;
}

.training {
  display: flex;
  flex-direction: column;
  height: 100dvh;
}

.header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: calc(env(safe-area-inset-top) + 24px) 24px 24px 24px;
  background-color: #fff;
  border-bottom: 1px solid #e5e5e5;
}

.question-count {
  font-size: 16px;
  color: #31373f;
}

h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 40px;
  align-items: center;
  justify-content: flex-start;
  padding: 24px;
  overflow: auto;

  .question {
    width: 100%;
    font-size: 16px;
    line-height: 24px;
    color: #31373f;
    white-space: pre-wrap;
  }

  .choices {
    width: 100%;
    border: 1px solid #e5e5e5;
    border-radius: 8px;

    :deep(.radio-group) {
      margin-top: 0 !important;
    }
  }
}

.bottom {
  display: flex;
  justify-content: center;
  padding: 24px 24px calc(env(safe-area-inset-bottom) + 24px) 24px;
  border-top: 1px solid #e5e5e5;

  .next-button {
    width: 100%;
    font-size: 16px;
  }
}
</style>
